import { HANDLE_PATTERN, IMAGE_SRC_PATTERN, WA_PATTERN, WEEKDAYS, isSafeHref, type SiteInfo } from "@baze/schema";

// ── Links ──────────────────────────────────────────────────────────────────────

/** Re-check an href at render time. Returns the href if allow-listed, otherwise null (render no link). */
export function safeHref(value: string | undefined | null): string | null {
  return isSafeHref(value) ? value : null;
}

/** True for absolute http(s) links, which open in a new tab with rel="noopener noreferrer". */
export function isExternalHref(href: string): boolean {
  return /^https?:\/\//i.test(href);
}

export interface MessageVars {
  nama_usaha?: string;
  produk?: string;
}

/** Substitute {{nama_usaha}} / {{produk}}; unknown or missing placeholders are removed. */
export function fillMessage(template: string, vars: MessageVars = {}): string {
  return template
    .replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (_, key: string) => (key === "nama_usaha" || key === "produk" ? (vars[key] ?? "") : ""))
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\s+([,.!?])/g, "$1")
    .trim();
}

/**
 * wa.me deep link: `https://wa.me/<digits>?text=<encoded message>`.
 * Returns null when the number is not a valid Indonesian WhatsApp number (62…).
 */
export function waLink(number: string | undefined | null, message?: string | null, vars: MessageVars = {}): string | null {
  const digits = (number ?? "").replace(/[^0-9]/g, "");
  if (!WA_PATTERN.test(digits)) return null;
  const text = message ? fillMessage(message, vars) : "";
  return text ? `https://wa.me/${digits}?text=${encodeURIComponent(text)}` : `https://wa.me/${digits}`;
}

export type SocialKind = keyof SiteInfo["social"];
export const SOCIAL_KINDS: readonly SocialKind[] = ["instagram", "tiktok", "facebook", "youtube"];
export const SOCIAL_LABELS: Record<SocialKind, string> = {
  instagram: "Instagram",
  tiktok: "TikTok",
  facebook: "Facebook",
  youtube: "YouTube",
};

/** Profile URL for a social handle (handles, never URLs, are stored). Null if the handle is invalid. */
export function socialUrl(kind: SocialKind, handle: string | undefined | null): string | null {
  const h = (handle ?? "").replace(/^@/, "");
  if (!HANDLE_PATTERN.test(h)) return null;
  switch (kind) {
    case "instagram":
      return `https://www.instagram.com/${h}/`;
    case "tiktok":
      return `https://www.tiktok.com/@${h}`;
    case "facebook":
      return `https://www.facebook.com/${h}`;
    case "youtube":
      return `https://www.youtube.com/@${h}`;
    default:
      return null;
  }
}

export function socialLinks(site: SiteInfo): Array<{ kind: SocialKind; label: string; href: string; handle: string }> {
  const out: Array<{ kind: SocialKind; label: string; href: string; handle: string }> = [];
  for (const kind of SOCIAL_KINDS) {
    const handle = site.social?.[kind];
    const href = socialUrl(kind, handle);
    if (href && handle) out.push({ kind, label: SOCIAL_LABELS[kind], href, handle });
  }
  return out;
}

export function telHref(phone: string | undefined | null): string | null {
  if (!phone) return null;
  const digits = phone.replace(/[^0-9+]/g, "");
  return safeHref(`tel:${digits}`);
}

export function mailHref(email: string | undefined | null): string | null {
  if (!email || !/^[^\s@<>"'`\\]+@[^\s@<>"'`\\]+$/.test(email)) return null;
  return safeHref(`mailto:${email}`);
}

/** Query used for maps: coordinates when known, otherwise "address, city". */
export function mapsQuery(contact: SiteInfo["contact"]): string | null {
  if (contact.geo) return `${contact.geo.lat},${contact.geo.lng}`;
  const parts = [contact.address, contact.city].filter((s): s is string => !!s && s.trim().length > 0);
  if (!contact.address || parts.length === 0) return null;
  return parts.join(", ");
}

export function mapsEmbedUrl(contact: SiteInfo["contact"]): string | null {
  const q = mapsQuery(contact);
  return q ? `https://www.google.com/maps?q=${encodeURIComponent(q)}&output=embed` : null;
}

export function directionsUrl(contact: SiteInfo["contact"]): string | null {
  const own = safeHref(contact.mapsUrl);
  if (own) return own;
  const q = mapsQuery(contact) ?? (contact.city ? contact.city : null);
  return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : null;
}

// ── Money ──────────────────────────────────────────────────────────────────────

/** 25000 → "Rp 25.000". Deterministic (no Intl) so server and client output match. */
export function formatRupiah(value: number): string {
  if (!Number.isFinite(value)) return "";
  const n = Math.round(Math.abs(value));
  const grouped = String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${value < 0 ? "-" : ""}Rp ${grouped}`;
}

// ── Images ─────────────────────────────────────────────────────────────────────

export interface ImageLike {
  src: string;
  alt?: string;
  width?: number;
  height?: number;
  focal?: { x: number; y: number };
}

export const SRCSET_WIDTHS = [480, 960, 1600, 2400] as const;
const MEDIA_VARIANT = /^(\/media\/sites\/[A-Za-z0-9-]+\/[A-Za-z0-9-]+\/)([0-9]{2,5})\.webp$/;
const UNSPLASH = /^https:\/\/images\.unsplash\.com\//;

/** Image src if it passes the schema allow-list (https or /media), else null. */
export function safeImageSrc(src: string | undefined | null): string | null {
  return typeof src === "string" && src.length <= 2048 && IMAGE_SRC_PATTERN.test(src) ? src : null;
}

function widthsFor(max: number | undefined): number[] {
  const ws = SRCSET_WIDTHS.filter((w) => max === undefined || w <= max);
  if (ws.length === 0) return [SRCSET_WIDTHS[0]];
  return ws;
}

function unsplashUrl(src: string, w: number): string {
  const base = src.split("?")[0]!;
  return `${base}?auto=format&fit=crop&w=${w}&q=75`;
}

/** Candidate URLs by width for our /media variants and Unsplash; empty for other sources. */
function candidates(image: ImageLike): Array<{ url: string; w: number }> {
  const src = safeImageSrc(image.src);
  if (!src) return [];
  const media = MEDIA_VARIANT.exec(src);
  if (media) {
    const max = image.width ?? Number(media[2]);
    return widthsFor(max).map((w) => ({ url: `${media[1]}${w}.webp`, w }));
  }
  if (UNSPLASH.test(src)) return widthsFor(image.width).map((w) => ({ url: unsplashUrl(src, w), w }));
  return [];
}

/** `srcset` for an image, or undefined when the source has no known resize variants. */
export function srcSet(image: ImageLike): string | undefined {
  const list = candidates(image);
  return list.length ? list.map((c) => `${c.url} ${c.w}w`).join(", ") : undefined;
}

/** Fallback `src`: the variant closest to `target` px (default 960) without exceeding the original. */
export function imageSrc(image: ImageLike, target = 960): string | null {
  const src = safeImageSrc(image.src);
  if (!src) return null;
  const list = candidates(image);
  if (!list.length) return src;
  const fit = list.filter((c) => c.w <= target);
  return (fit[fit.length - 1] ?? list[0]!).url;
}

export function objectPosition(image: ImageLike): string | undefined {
  return image.focal ? `${image.focal.x}% ${image.focal.y}%` : undefined;
}

// ── Opening hours ──────────────────────────────────────────────────────────────

export type Weekday = (typeof WEEKDAYS)[number];
export const DAY_LABELS: Record<Weekday, string> = {
  mon: "Senin",
  tue: "Selasa",
  wed: "Rabu",
  thu: "Kamis",
  fri: "Jumat",
  sat: "Sabtu",
  sun: "Minggu",
};
export const SCHEMA_DAYS: Record<Weekday, string> = {
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
  sun: "Sunday",
};

export type HoursEntry = NonNullable<SiteInfo["hours"]>[number];

/** "08:00" → "08.00" (Indonesian time notation). */
export const formatTime = (t: string) => t.replace(":", ".");

/** Display value for one day: "08.00–22.00", "Tutup", or "24 jam". */
export function hoursLabel(entry: HoursEntry | undefined): string {
  if (!entry) return "—";
  if (entry.closed) return "Tutup";
  if (!entry.open || !entry.close) return "Buka";
  if (entry.open === "00:00" && (entry.close === "00:00" || entry.close === "23:59")) return "24 jam";
  return `${formatTime(entry.open)}–${formatTime(entry.close)}`;
}

/** Hours in weekday order (Senin first), one entry per provided day. */
export function sortedHours(hours: SiteInfo["hours"]): HoursEntry[] {
  const byDay = new Map((hours ?? []).map((h) => [h.day, h]));
  return WEEKDAYS.flatMap((d) => (byDay.has(d) ? [byDay.get(d)!] : []));
}

/** Consecutive days with identical hours grouped: [{label:"Senin – Kamis", value:"08.00–22.00"}]. */
export function groupedHours(hours: SiteInfo["hours"]): Array<{ label: string; value: string; closed: boolean }> {
  const list = sortedHours(hours);
  const groups: Array<{ from: Weekday; to: Weekday; value: string; closed: boolean }> = [];
  for (const h of list) {
    const value = hoursLabel(h);
    const last = groups[groups.length - 1];
    const prevIndex = last ? WEEKDAYS.indexOf(last.to) : -2;
    if (last && last.value === value && WEEKDAYS.indexOf(h.day) === prevIndex + 1) last.to = h.day;
    else groups.push({ from: h.day, to: h.day, value, closed: h.closed });
  }
  return groups.map((g) => ({
    label: g.from === g.to ? DAY_LABELS[g.from] : `${DAY_LABELS[g.from]} – ${DAY_LABELS[g.to]}`,
    value: g.value,
    closed: g.closed,
  }));
}

// ── Text ───────────────────────────────────────────────────────────────────────

/** Split multi-line text into paragraphs (blank line) and lines (single newline). */
export function paragraphs(text: string | undefined | null): string[][] {
  if (!text) return [];
  return text
    .replace(/\r\n?/g, "\n")
    .split(/\n{2,}/)
    .map((p) => p.split("\n").map((l) => l.trim()).filter(Boolean))
    .filter((p) => p.length > 0);
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "")).toUpperCase() || "•";
}

// ── Dates (events) ─────────────────────────────────────────────────────────────

const MONTHS_ID = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"] as const;
const WEEKDAYS_ID = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"] as const;

export interface EventDate {
  year: number;
  month: number;
  day: number;
  weekday: string;
  monthName: string;
  /** "Sabtu, 14 Desember 2026" */
  long: string;
  /** "20261214" */
  compact: string;
}

/** Parse YYYY-MM-DD into Indonesian labels. Null when the string is not a real calendar date. */
export function parseEventDate(value: string | undefined | null): EventDate | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? "");
  if (!m) return null;
  const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const d = new Date(Date.UTC(year, month - 1, day));
  if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) return null;
  const weekday = WEEKDAYS_ID[d.getUTCDay()]!;
  const monthName = MONTHS_ID[month - 1]!;
  return { year, month, day, weekday, monthName, long: `${weekday}, ${day} ${monthName} ${year}`, compact: m[1]! + m[2]! + m[3]! };
}

/** UTC offset in hours of an Indonesian time note (WIB +7, WITA +8, WIT +9; no daylight saving). */
function offsetFor(note: string | undefined): number {
  const n = (note ?? "").toUpperCase();
  if (/\bWITA\b/.test(n)) return 8;
  if (/\bWIT\b/.test(n)) return 9;
  return 7;
}

/** The instant (ms since epoch) of a local date + time in the note's zone; midnight when no time. */
export function eventInstant(date: string | undefined | null, time?: string, timeNote?: string): number | null {
  const d = parseEventDate(date);
  if (!d) return null;
  const [h, m] = time && /^\d{2}:\d{2}$/.test(time) ? (time.split(":").map(Number) as [number, number]) : [0, 0];
  return Date.UTC(d.year, d.month - 1, d.day, h - offsetFor(timeNote), m);
}

/** Days / hours / minutes / seconds left until `at`; all zero once it has passed. */
export function timeLeft(at: number, now: number): { done: boolean; d: number; h: number; m: number; s: number } {
  const t = Math.max(0, Math.floor((at - now) / 1000));
  return { done: t === 0, d: Math.floor(t / 86400), h: Math.floor((t % 86400) / 3600), m: Math.floor((t % 3600) / 60), s: t % 60 };
}

/** IANA zone from an Indonesian time note ("WIB", "WITA", "WIT"); WIB by default. */
function zoneFor(note: string | undefined): string {
  const n = (note ?? "").toUpperCase();
  if (/\bWITA\b/.test(n)) return "Asia/Makassar";
  if (/\bWIT\b/.test(n)) return "Asia/Jayapura";
  return "Asia/Jakarta";
}

const addMinutes = (date: EventDate, hhmm: string, minutes: number) => {
  const [h, mi] = hhmm.split(":").map(Number) as [number, number];
  const t = new Date(Date.UTC(date.year, date.month - 1, date.day, h, mi + minutes));
  const p = (n: number) => String(n).padStart(2, "0");
  return `${t.getUTCFullYear()}${p(t.getUTCMonth() + 1)}${p(t.getUTCDate())}T${p(t.getUTCHours())}${p(t.getUTCMinutes())}00`;
};

/**
 * "Add to Google Calendar" template link. Times are local wall-clock in the event's zone (ctz);
 * without a start time the event is all-day; without an end time it lasts two hours.
 */
export function googleCalendarUrl(ev: { name: string; date?: string; startTime?: string; endTime?: string; timeNote?: string; venue?: string; address?: string }, siteName: string): string | null {
  const date = parseEventDate(ev.date);
  if (!date) return null;
  let dates: string;
  if (ev.startTime) {
    const start = addMinutes(date, ev.startTime, 0);
    const end = ev.endTime && ev.endTime > ev.startTime ? addMinutes(date, ev.endTime, 0) : addMinutes(date, ev.startTime, 120);
    dates = `${start}/${end}`;
  } else {
    const next = new Date(Date.UTC(date.year, date.month - 1, date.day + 1));
    const p = (n: number) => String(n).padStart(2, "0");
    dates = `${date.compact}/${next.getUTCFullYear()}${p(next.getUTCMonth() + 1)}${p(next.getUTCDate())}`;
  }
  const params = new URLSearchParams({ action: "TEMPLATE", text: `${ev.name} – ${siteName}`.slice(0, 200), dates, ctz: zoneFor(ev.timeNote) });
  const location = [ev.venue, ev.address].filter(Boolean).join(", ");
  if (location) params.set("location", location.slice(0, 300));
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/** "08.00 – 10.00 WIB", "19.00 WIB – selesai" style label from the optional event time fields. */
export function eventTimeLabel(ev: { startTime?: string; endTime?: string; timeNote?: string }): string | null {
  if (!ev.startTime && !ev.timeNote) return null;
  const range = ev.startTime ? (ev.endTime ? `${formatTime(ev.startTime)} – ${formatTime(ev.endTime)}` : formatTime(ev.startTime)) : "";
  return [range, ev.timeNote].filter(Boolean).join(" ");
}

// ── Stores (links, affiliate picks) ────────────────────────────────────────────

export type StoreKind = "shop" | "tiktok" | "instagram" | "youtube" | "facebook" | "whatsapp" | "support" | "music" | "email" | "phone" | "link";

export interface StoreInfo {
  kind: StoreKind;
  /** Display name ("Shopee", "TikTok"); null when the host is not a known store or platform. */
  name: string | null;
}

/** Known hosts → display name. Text labels only, never brand logos. Host match includes subdomains. */
const STORES: Array<{ hosts: string[]; kind: StoreKind; name: string }> = [
  { hosts: ["shopee.co.id", "shope.ee", "shopee.com"], kind: "shop", name: "Shopee" },
  { hosts: ["tokopedia.com", "tokopedia.link", "tkp.me"], kind: "shop", name: "Tokopedia" },
  { hosts: ["lazada.co.id", "lzd.co"], kind: "shop", name: "Lazada" },
  { hosts: ["blibli.com"], kind: "shop", name: "Blibli" },
  { hosts: ["bukalapak.com"], kind: "shop", name: "Bukalapak" },
  { hosts: ["amazon.com", "amzn.to"], kind: "shop", name: "Amazon" },
  { hosts: ["tiktok.com"], kind: "tiktok", name: "TikTok" },
  { hosts: ["instagram.com", "instagr.am"], kind: "instagram", name: "Instagram" },
  { hosts: ["youtube.com", "youtu.be"], kind: "youtube", name: "YouTube" },
  { hosts: ["facebook.com", "fb.me"], kind: "facebook", name: "Facebook" },
  { hosts: ["wa.me", "whatsapp.com"], kind: "whatsapp", name: "WhatsApp" },
  { hosts: ["saweria.co"], kind: "support", name: "Saweria" },
  { hosts: ["trakteer.id"], kind: "support", name: "Trakteer" },
  { hosts: ["spotify.com", "spotify.link"], kind: "music", name: "Spotify" },
  { hosts: ["music.apple.com"], kind: "music", name: "Apple Music" },
  { hosts: ["soundcloud.com"], kind: "music", name: "SoundCloud" },
  { hosts: ["bandcamp.com"], kind: "music", name: "Bandcamp" },
  { hosts: ["linkedin.com"], kind: "link", name: "LinkedIn" },
  { hosts: ["github.com"], kind: "link", name: "GitHub" },
  { hosts: ["behance.net"], kind: "link", name: "Behance" },
  { hosts: ["dribbble.com"], kind: "link", name: "Dribbble" },
];

/** Recognise the store or platform behind a link, for icons and "Lihat di Shopee" labels. */
export function storeFromHref(href: string | undefined | null): StoreInfo {
  const safe = safeHref(href);
  if (!safe) return { kind: "link", name: null };
  if (safe.startsWith("mailto:")) return { kind: "email", name: null };
  if (safe.startsWith("tel:")) return { kind: "phone", name: null };
  let host: string;
  try {
    host = new URL(safe).hostname.toLowerCase();
  } catch {
    return { kind: "link", name: null };
  }
  const hit = STORES.find((s) => s.hosts.some((h) => host === h || host.endsWith(`.${h}`)));
  return hit ? { kind: hit.kind, name: hit.name } : { kind: "link", name: null };
}
